import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getSuperAdminMetrics } from "../config/url";
import { DEFAULT_DATE_RANGE_KEY, getDateRangeForPreset } from "../utils/dateRangePresets";
import classes from "./Dashboard.module.css";
import MetricCard from "../components/dashboard/MetricCard";
import superClasses from "./SuperAdminDashboard.module.css";

const emptyMetrics = {
	activeOrganizations: 0,
	activeUsers: 0,
	totalEncounterForms: 0,
	totalVisits: 0,
};

const toCount = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => toCount(value).toLocaleString("en-US");

const SuperAdminDashboard = () => {
	const userState = useSelector((state) => state?.user);
	const token = userState?.user?.token || localStorage.getItem("user_id");

	const [hasFullError, setHasFullError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [metricErrors, setMetricErrors] = useState({});
	const [metrics, setMetrics] = useState(emptyMetrics);

	const reportingRange = useMemo(() => getDateRangeForPreset(DEFAULT_DATE_RANGE_KEY), []);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Cache-Control": "no-cache",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	const fetchMetrics = useCallback(async () => {
		if (!token) {
			setIsLoading(false);
			setHasFullError(true);
			setMetrics(emptyMetrics);
			setMetricErrors({});
			return;
		}

		setIsLoading(true);
		setHasFullError(false);

		try {
			const response = await axios.get(getSuperAdminMetrics, {
				...requestConfig,
				params: {
					_: Date.now(),
					endDate: reportingRange.endDate,
					startDate: reportingRange.startDate,
				},
			});

			if (response?.data?.success !== true) {
				throw new Error(response?.data?.message || "Failed to load dashboard metrics");
			}

			const data = response?.data?.data || {};

			setMetrics({
				activeOrganizations: toCount(data.activeOrganizations),
				activeUsers: toCount(data.activeUsers),
				totalEncounterForms: toCount(data.totalEncounterForms),
				totalVisits: toCount(data.totalVisits),
			});
			setMetricErrors(response?.data?.metricErrors || {});
		} catch {
			setHasFullError(true);
			setMetricErrors({});
			setMetrics(emptyMetrics);
		} finally {
			setIsLoading(false);
		}
	}, [reportingRange.endDate, reportingRange.label, reportingRange.startDate, requestConfig, token]);

	useEffect(() => {
		fetchMetrics();
	}, [fetchMetrics]);

	const getMetricValue = (key, value) => {
		if (isLoading) return "";
		if (metricErrors[key]) return "—";
		return formatCount(value);
	};

	return (
		<div className={classes.page}>
			<div className={classes.headerRow}>
				<div className={classes.titleBlock}>
					<h1 className={classes.title}>Dashboard</h1>
					<p className={classes.subtitle}>Monitor service verification and compliance activity.</p>
				</div>
			</div>

			{hasFullError ? (
				<div className={superClasses.errorBox}>
					<p className={superClasses.errorTitle}>Dashboard metrics could not be loaded</p>
					<button className={superClasses.retryButton} onClick={fetchMetrics} type="button">
						Try Again
					</button>
				</div>
			) : null}

			<div className={superClasses.metricsGrid}>
				<MetricCard
					interactive={false}
					isLoading={isLoading}
					title="Active Organizations"
					value={getMetricValue("activeOrganizations", metrics.activeOrganizations)}
				/>
				<MetricCard
					interactive={false}
					isLoading={isLoading}
					title="Active Users"
					value={getMetricValue("activeUsers", metrics.activeUsers)}
				/>
				<MetricCard
					interactive={false}
					isLoading={isLoading}
					title="Total Visits"
					value={getMetricValue("totalVisits", metrics.totalVisits)}
				/>
				<MetricCard
					interactive={false}
					isLoading={isLoading}
					title="Total Encounter Forms"
					value={getMetricValue("totalEncounterForms", metrics.totalEncounterForms)}
				/>
			</div>
		</div>
	);
};

export default SuperAdminDashboard;
